import { FormField } from "@/components/ui/form";
import { useFormContext } from "react-hook-form";
import { PaymentDetails } from "./PaymentDetails";
import { PersonalDetailsSection } from "../sections/PersonalDetailsSection";
import { BankingDetailsSection } from "../sections/BankingDetailsSection";
import { DocumentUploadSection } from "../sections/DocumentUploadSection";
import { ServiceSelectionSection } from "../sections/ServiceSelectionSection";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface FormContentProps {
  currentStep: number;
  onPaymentSuccess: (paymentIntentId: string) => void;
}

export function FormContent({ currentStep, onPaymentSuccess }: FormContentProps) {
  const form = useFormContext();
  const [amount, setAmount] = useState<number>(0);
  const isPaymentStep = currentStep === 4;
  const paymentStatus = form.watch('payment_status');
  const taxReturnId = form.watch('id');
  const serviceId = form.watch('service_id');

  useEffect(() => {
    async function fetchServiceAmount() {
      if (serviceId) {
        const { data: service, error } = await supabase
          .from('services')
          .select('amount, gst_amount')
          .eq('id', serviceId)
          .single();

        if (!error && service) {
          setAmount(service.amount + (service.gst_amount || 0));
        }
      }
    }

    fetchServiceAmount();
  }, [serviceId]);

  if (isPaymentStep) {
    if (!taxReturnId || !serviceId) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Unable to process payment: Required information is missing. Please try again or contact support.
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="space-y-6">
        {paymentStatus && (
          <PaymentStatusBadge status={paymentStatus} className="mb-4" />
        )}
        <PaymentDetails
          amount={amount}
          taxReturnId={taxReturnId}
          serviceId={serviceId}
          onPaymentSuccess={onPaymentSuccess}
        />
      </div>
    );
  }

  const components = [
    PersonalDetailsSection,
    BankingDetailsSection,
    DocumentUploadSection,
    ServiceSelectionSection,
  ];

  const CurrentStepComponent = components[currentStep];
  return <CurrentStepComponent />;
}